import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ArrowLeft, User, Car, Wrench, Droplets } from "lucide-react";

const inputCls =
  "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6 md:p-8">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#E30613]/15 text-[#FF1C2A]">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </span>
        <h3 className="font-display text-base font-semibold uppercase tracking-wider text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-neutral-300">
        {label} {required && <span className="text-[#FF1C2A]">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-[#FF6069]">{error}</p>}
    </div>
  );
}

export default function CreateCertificate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [products, setProducts] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [selectedInstaller, setSelectedInstaller] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [balance, setBalance] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [idemKey, setIdemKey] = useState("");
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    vin: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_color: "",
    license_plate: "",
    product_installed: "",
    installation_date: new Date().toISOString().slice(0, 10),
    installation_package: "",
    installer_notes: "",
  });

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      if (data.length === 1) setForm((f) => ({ ...f, product_installed: data[0].name }));
    }).catch(() => {});
    if (isAdmin) {
      api.get("/users").then(({ data }) => {
        setInstallers(data.filter((u) => u.role === "installer" && u.status === "active"));
      }).catch(() => {});
    } else {
      api.get("/credits/balance").then(({ data }) => setBalance(data.available_credits)).catch(() => {});
    }
  }, [isAdmin]);

  const pickInstaller = (id) => {
    const inst = installers.find((u) => u.id === id) || null;
    setSelectedInstaller(inst);
    setBalance(inst ? inst.certificate_credits : null);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = "Customer name is required.";
    if (!form.customer_email.trim()) e.customer_email = "Customer email is required.";
    else if (!EMAIL_RE.test(form.customer_email)) e.customer_email = "Enter a valid email address.";
    if (!form.vin.trim()) e.vin = "VIN is required.";
    else if (form.vin.trim().length !== 17) e.vin = "VIN must be exactly 17 characters.";
    if (!form.vehicle_year.trim()) e.vehicle_year = "Vehicle year is required.";
    if (!form.vehicle_make.trim()) e.vehicle_make = "Vehicle make is required.";
    if (!form.vehicle_model.trim()) e.vehicle_model = "Vehicle model is required.";
    if (!form.product_installed) e.product_installed = "Select a product.";
    if (!form.installation_package) e.installation_package = "Select a package.";
    if (!form.installation_date) e.installation_date = "Installation date is required.";
    if (isAdmin && !selectedInstaller) e.installer_id = "Select an installer to issue for.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openConfirm = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    if (balance !== null && balance < 1) {
      toast.error("You do not have enough certificate credits to generate this certificate.");
      return;
    }
    setIdemKey(crypto.randomUUID());
    setConfirmOpen(true);
  };

  const doGenerate = async () => {
    setSaving(true);
    try {
      const payload = { ...form, vin: form.vin.toUpperCase(), idempotency_key: idemKey };
      if (isAdmin && selectedInstaller) payload.installer_id = selectedInstaller.id;
      const { data } = await api.post("/installations", payload);
      toast.success(`Certificate ${data.certificate_number} created`);
      navigate(`/certificates/${data.id}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) {
        toast.error("You do not have enough certificate credits to generate this certificate.");
        setConfirmOpen(false);
        navigate("/bottle-activations");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create certificate");
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="New Installation Certificate">
      <button
        onClick={() => navigate("/certificates")}
        className="mb-5 inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-white"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Back to history
      </button>

      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Certificate of Installation</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">New installation record</h2>
        <p className="mt-1 text-sm text-neutral-500">A unique certificate number will be generated on submit. This uses 1 certificate credit.</p>
      </div>

      {balance !== null && balance < 1 && (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-[#E30613]/30 bg-[#E30613]/10 px-5 py-4 text-sm text-[#FF6069] sm:flex-row sm:items-center sm:justify-between" data-testid="no-credits-banner">
          <span>You do not have enough certificate credits to generate this certificate.</span>
          <button onClick={() => navigate("/bottle-activations")} className="inline-flex items-center gap-2 self-start rounded-lg bg-[#E30613] px-4 py-2 text-xs font-medium text-white hover:bg-[#FF1C2A] sm:self-auto" data-testid="banner-activate-btn">
            <Droplets className="h-3.5 w-3.5" /> Activate Bottle
          </button>
        </div>
      )}

      <form onSubmit={openConfirm} className="max-w-4xl space-y-6" data-testid="certificate-form">
        {isAdmin && (
          <Section icon={Wrench} title="Issue For Installer">
            <Field label="Installer" required error={errors.installer_id}>
              <Select value={selectedInstaller?.id || ""} onValueChange={pickInstaller}>
                <SelectTrigger className={inputCls} data-testid="field-installer">
                  <SelectValue placeholder="Select an installer" />
                </SelectTrigger>
                <SelectContent className="max-h-72 border-white/[0.08] bg-[#141414] text-white">
                  {installers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="focus:bg-white/[0.06]" data-testid={`installer-option-${u.id}`}>
                      {u.business_name} — {u.certificate_credits} credits
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedInstaller && <p className="text-xs text-neutral-500">This certificate will use 1 credit from {selectedInstaller.business_name}.</p>}
            </Field>
          </Section>
        )}
        <Section icon={User} title="Customer Information">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="Customer Full Name" required error={errors.customer_name}>
              <Input value={form.customer_name} onChange={set("customer_name")} className={inputCls} data-testid="field-customer-name" />
            </Field>
            <Field label="Customer Email" required error={errors.customer_email}>
              <Input type="email" value={form.customer_email} onChange={set("customer_email")} className={inputCls} data-testid="field-customer-email" />
            </Field>
            <Field label="Customer Phone Number">
              <Input value={form.customer_phone} onChange={set("customer_phone")} className={inputCls} data-testid="field-customer-phone" />
            </Field>
          </div>
        </Section>

        <Section icon={Car} title="Vehicle Information">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="VIN" required error={errors.vin}>
              <Input
                value={form.vin}
                onChange={(e) => setForm((f) => ({ ...f, vin: e.target.value.toUpperCase() }))}
                maxLength={17}
                placeholder="17-character VIN"
                className={`${inputCls} font-mono uppercase`}
                data-testid="field-vin"
              />
              <p className="text-xs text-neutral-600">{form.vin.length}/17</p>
            </Field>
            <Field label="Vehicle Year" required error={errors.vehicle_year}>
              <Input value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2026" className={inputCls} data-testid="field-vehicle-year" />
            </Field>
            <Field label="Vehicle Make" required error={errors.vehicle_make}>
              <Input value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Porsche" className={inputCls} data-testid="field-vehicle-make" />
            </Field>
            <Field label="Vehicle Model" required error={errors.vehicle_model}>
              <Input value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="911 Carrera" className={inputCls} data-testid="field-vehicle-model" />
            </Field>
            <Field label="Vehicle Color">
              <Input value={form.vehicle_color} onChange={set("vehicle_color")} className={inputCls} data-testid="field-vehicle-color" />
            </Field>
            <Field label="License Plate">
              <Input value={form.license_plate} onChange={set("license_plate")} className={inputCls} data-testid="field-license-plate" />
            </Field>
          </div>
        </Section>

        <Section icon={Wrench} title="Installation Information">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="Installation Date" required error={errors.installation_date}>
              <Input type="date" value={form.installation_date} onChange={set("installation_date")} className={`${inputCls} [color-scheme:dark]`} data-testid="field-installation-date" />
            </Field>
            <Field label="Product Installed" required error={errors.product_installed}>
              <Select value={form.product_installed} onValueChange={(v) => setForm((f) => ({ ...f, product_installed: v }))}>
                <SelectTrigger className={inputCls} data-testid="field-product">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.name} className="focus:bg-white/[0.06]" data-testid={`product-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Installation Package" required error={errors.installation_package}>
              <Select value={form.installation_package} onValueChange={(v) => setForm((f) => ({ ...f, installation_package: v }))}>
                <SelectTrigger className={inputCls} data-testid="field-package">
                  <SelectValue placeholder="Select a package" />
                </SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                  {["Essential", "Complete", "Ultimate"].map((p) => (
                    <SelectItem key={p} value={p} className="focus:bg-white/[0.06]" data-testid={`package-option-${p}`}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Installer Notes">
                <Textarea
                  value={form.installer_notes}
                  onChange={set("installer_notes")}
                  rows={4}
                  placeholder="Optional notes about the installation…"
                  className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]"
                  data-testid="field-notes"
                />
              </Field>
            </div>
          </div>
        </Section>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving || (balance !== null && balance < 1)}
            className="flex h-11 items-center justify-center rounded-lg bg-[#E30613] px-7 font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A] disabled:opacity-60"
            data-testid="submit-certificate"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Certificate"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/certificates")}
            className="h-11 rounded-lg border border-white/[0.1] px-6 text-sm font-medium text-neutral-300 transition-colors hover:bg-white/[0.05]"
          >
            Cancel
          </button>
        </div>
      </form>

      <Dialog open={confirmOpen} onOpenChange={(o) => !saving && setConfirmOpen(o)}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="confirm-generate-modal">
          <DialogHeader><DialogTitle>Confirm Certificate Generation</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-400">Generating this installation certificate will use 1 certificate credit.</p>
          <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4 text-sm">
            <div className="flex justify-between text-neutral-400"><span>Current Balance</span><span className="text-white" data-testid="confirm-current-balance">{balance}</span></div>
            <div className="mt-1 flex justify-between border-t border-white/[0.06] pt-2 font-medium"><span className="text-neutral-300">Balance After Generation</span><span className="text-white" data-testid="confirm-balance-after">{balance !== null ? balance - 1 : "—"}</span></div>
          </div>
          <DialogFooter>
            <button onClick={() => setConfirmOpen(false)} disabled={saving} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={doGenerate} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-generate-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Certificate"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
